import { use, useEffect, useState } from 'react';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';
import { DateRange } from 'shared/components/date-range-picker/models/date-range';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import OrganisationUnit from 'features/organisation/domain/models/organisation-unit';
import Role from 'features/role/domain/models/role';
import { CreateTemporaryUserCommand } from '../domain/models/create-temporary-user-command';
import ReadTemporaryAccessResponse from '../domain/models/read-temporary-access-response';
import TemporaryAccess from '../domain/models/temporary-access';
import { TemporaryAccessType } from '../domain/models/temporary-access-type';
import TemporaryUser from '../domain/models/temporary-user';
import { ValidateTemporaryUserPeriodCommand } from '../domain/models/validate-temporary-user-period-command';
import {
  useCreateTemporaryUser,
  useLazyReadOrganizationUnitsForTemporaryAccess,
  useLazyReadRolesForTemporaryAccess,
  useLazyReadTemporaryAccess,
  useLazyReadTemporaryUser,
  useRevokeTemporaryChannelAccess,
  useRevokeTemporaryUser,
  validateTemporaryUserPeriod,
} from '../providers/temporary-access-queries';
import { useTemporaryUsersState } from './states/temporary-access-state';
import useTemporaryAccessFilters, { ITemporaryAccessFilterHook } from './temporary-access-filters-hook';

export interface ITemporaryAccessProviderHook {
  readTemporaryAccessData?: ReadTemporaryAccessResponse;
  readTemporaryAccessIsFetching: boolean;
  readTemporaryAccessIsSuccess: boolean;

  readTemporaryUserData?: TemporaryUser;
  readTemporaryUserIsFetching: boolean;
  readTemporaryUserIsSuccess: boolean;
  readTemporaryUserError?: Error;

  resetCreateTemporaryUserStatus: () => void;
  createTemporaryUser: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    organisationUnitId: string[],
    roleIdpId: string,
    period: DateRange,
  ) => Promise<boolean>;
  createTemporaryUserIsLoading: boolean;
  createTemporaryUserIsSuccess: boolean;
  createTemporaryUserIsError: boolean;

  revokeTemporaryAccess: (temporaryUser: TemporaryAccess) => void;
  revokeTemporaryAccessIsLoading: boolean;

  revokeTemporaryUserConfirmationPopupIsOpen: boolean;
  closeRevokeTemporaryUserConfirmationPopup: () => void;
  revokeTemporaryUserConfirmationAction: () => void;

  isTemporaryUserPeriodValid: (email: string, accessOrganisationUnitIds: string[], period: DateRange) => Promise<boolean>;

  readOrganisationUnitsForTemporaryAccessData?: Array<OrganisationUnit>;
  readOrganisationUnitsForTemporaryAccessIsSuccess: boolean;

  readRolesForTemporaryAccess: () => void;
  readRolesForTemporaryAccessData?: Array<Role>;
  readRolesForTemporaryAccessIsFetching: boolean;
  readRolesForTemporaryAccessIsSuccess: boolean;
  readRolesForTemporaryAccessError?: Error;

  temporaryUserDetailsPopupIsOpen: boolean;
  closeTemporaryUserDetailsPopup: () => void;
  openTemporaryUserDetailsPopup: (viewingMode: ViewingMode) => void;

  selectedTemporaryUserId: string | undefined;
  selectTemporaryUser: (temporaryUserId: string, viewingMode: ViewingMode) => void;

  detailsViewingMode: ViewingMode;

  refresh: () => void;
  temporaryAccessFilterHook: ITemporaryAccessFilterHook;
}

const useTemporaryAccess = (): ITemporaryAccessProviderHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { authenticatedInstance } = useAxiosClientsContextProvider();

  const {
    setTemporaryUserDetailsPopupIsOpen,
    temporaryUserDetailsPopupIsOpen,
    setRevokeTemporaryUserConfirmationPopupIsOpen,
    revokeTemporaryUserConfirmationPopupIsOpen,
    revokeTemporaryUserConfirmationAction,
    setRevokeTemporaryUserConfirmationAction,
    selectedTemporaryUserId,
    setSelectedTemporaryUserId,
  } = useTemporaryUsersState();

  const [detailsViewingMode, setDetailsViewingMode] = useState<ViewingMode>('none');
  const temporaryAccessFilterHook = useTemporaryAccessFilters();

  const {
    refetch: triggerReadTemporaryAccess,
    data: readTemporaryAccessData,
    isSuccess: readTemporaryAccessIsSuccess,
    isPending: readTemporaryAccessIsPending,
    error: readTemporaryAccessError,
  } = useLazyReadTemporaryAccess(temporaryAccessFilterHook.readTemporaryAccessQuery);

  const {
    refetch: triggerReadTemporaryUser,
    data: readTemporaryUserData,
    isFetching: readTemporaryUserIsFetching,
    isSuccess: readTemporaryUserIsSuccess,
    error: readTemporaryUserError,
  } = useLazyReadTemporaryUser(selectedTemporaryUserId ?? '');

  const {
    mutateAsync: createTemporaryUserMutateAsync,
    isPending: createTemporaryUserIsLoading,
    isSuccess: createTemporaryUserIsSuccess,
    isError: createTemporaryUserIsError,
    error: createTemporaryUserError,
    reset: resetCreateTemporaryUserStatus,
  } = useCreateTemporaryUser();

  const {
    mutateAsync: revokeTemporaryUserMutateAsync,
    isPending: revokeTemporaryUserIsLoading,
    isSuccess: revokeTemporaryUserIsSuccess,
    isError: revokeTemporaryUserIsError,
    error: revokeTemporaryUserError,
  } = useRevokeTemporaryUser();

  const {
    mutateAsync: revokeTemporaryChannelAccessMutateAsync,
    isPending: revokeTemporaryChannelAccessIsLoading,
    isSuccess: revokeTemporaryChannelAccessIsSuccess,
    isError: revokeTemporaryChannelAccessIsError,
    error: revokeTemporaryChannelAccessError,
  } = useRevokeTemporaryChannelAccess();

  const {
    refetch: triggerReadOrganisationUnitsForTemporaryAccess,
    data: readOrganisationUnitsForTemporaryAccessData,
    isSuccess: readOrganisationUnitsForTemporaryAccessIsSuccess,
  } = useLazyReadOrganizationUnitsForTemporaryAccess();

  const {
    refetch: triggerReadRolesForTemporaryAccess,
    data: readRolesForTemporaryAccessData,
    isSuccess: readRolesForTemporaryAccessIsSuccess,
    isFetching: readRolesForTemporaryAccessIsFetching,
    error: readRolesForTemporaryAccessError,
  } = useLazyReadRolesForTemporaryAccess();

  const refresh = (): void => {
    triggerReadTemporaryAccess();
  };

  useEffect(() => {
    readOrganisationUnitsForTemporaryAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (readTemporaryAccessError) {
      setErrorMessage(readTemporaryAccessError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readTemporaryAccessError]);

  useEffect(() => {
    refresh();
  }, [temporaryAccessFilterHook.readTemporaryAccessQuery]);

  useEffect(() => {
    if (revokeTemporaryUserIsSuccess) {
      setRevokeTemporaryUserConfirmationPopupIsOpen(false);
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revokeTemporaryUserIsSuccess]);

  useEffect(() => {
    if (revokeTemporaryChannelAccessIsSuccess) {
      setRevokeTemporaryUserConfirmationPopupIsOpen(false);
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revokeTemporaryChannelAccessIsSuccess]);

  useEffect(() => {
    if (revokeTemporaryUserIsError && revokeTemporaryUserError) {
      setErrorMessage(revokeTemporaryChannelAccessError ?? undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revokeTemporaryUserIsError, revokeTemporaryUserError]);

  useEffect(() => {
    if (revokeTemporaryChannelAccessIsError && revokeTemporaryChannelAccessError) {
      setErrorMessage(revokeTemporaryUserError ?? undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revokeTemporaryChannelAccessIsError, revokeTemporaryChannelAccessError]);

  useEffect(() => {
    if (createTemporaryUserIsError && createTemporaryUserError) {
      setErrorMessage(createTemporaryUserError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTemporaryUserIsError, createTemporaryUserError]);

  useEffect(() => {
    if (selectedTemporaryUserId) triggerReadTemporaryUser();
  }, [selectedTemporaryUserId]);

  const createTemporaryUser = async (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    organisationUnitIds: string[],
    roleIdpId: string,
    period: DateRange,
  ): Promise<boolean> => {
    const createTemporaryUserCommand = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phoneNumber: phone.trim(),
      accessOrganisationUnitIds: organisationUnitIds,
      roleIdpId: roleIdpId,
      period: {
        startsOn: period.startDate,
        endsOn: period.endDate,
      },
    } as CreateTemporaryUserCommand;

    await createTemporaryUserMutateAsync(createTemporaryUserCommand);

    return true;
  };

  const revokeTemporaryAccess = (temporaryAccess: TemporaryAccess) => {
    setRevokeTemporaryUserConfirmationPopupIsOpen(true);
    setRevokeTemporaryUserConfirmationAction(() => () => confirmRevokeTemporaryAccess(temporaryAccess));
  };

  const confirmRevokeTemporaryAccess = async (temporaryAccess: TemporaryAccess) => {
    if (temporaryAccess.type === TemporaryAccessType.User) {
      await revokeTemporaryUserMutateAsync({
        temporaryUserId: temporaryAccess.id,
      });
    } else {
      await revokeTemporaryChannelAccessMutateAsync({
        temporaryChannelAccessId: temporaryAccess.id,
      });
    }
    setRevokeTemporaryUserConfirmationAction(() => () => {});
  };

  const closeRevokeTemporaryUserConfirmationPopup = () => {
    setRevokeTemporaryUserConfirmationPopupIsOpen(false);
  };

  const isTemporaryUserPeriodValid = async (email: string, accessOrganisationUnitIds: string[], period: DateRange): Promise<boolean> => {
    if (email && accessOrganisationUnitIds && period) {
      return await validateTemporaryUserPeriod(authenticatedInstance, {
        email: email,
        accessOrganisationUnitIds: accessOrganisationUnitIds,
        startsOn: period.startDate,
        endsOn: period.endDate,
      } as ValidateTemporaryUserPeriodCommand);
    }

    return true;
  };

  const readOrganisationUnitsForTemporaryAccess = () => {
    triggerReadOrganisationUnitsForTemporaryAccess();
  };

  const readRolesForTemporaryAccess = () => {
    triggerReadRolesForTemporaryAccess();
  };

  const openTemporaryUserDetailsPopup = (viewingMode: ViewingMode) => {
    setDetailsViewingMode(viewingMode);
    setTemporaryUserDetailsPopupIsOpen(true);
  };

  const closeTemporaryUserDetailsPopup = (): void => {
    setSelectedTemporaryUserId(undefined);
    setDetailsViewingMode('none');
    setTemporaryUserDetailsPopupIsOpen(false);
  };

  const selectTemporaryUser = (temporaryUserId: string, viewingMode: ViewingMode) => {
    setSelectedTemporaryUserId(temporaryUserId);
    openTemporaryUserDetailsPopup(viewingMode);
  };

  return {
    readTemporaryAccessData,
    readTemporaryAccessIsFetching: readTemporaryAccessIsPending,
    readTemporaryAccessIsSuccess,

    readTemporaryUserData,
    readTemporaryUserIsFetching,
    readTemporaryUserIsSuccess,
    readTemporaryUserError: readTemporaryUserError ?? undefined,

    resetCreateTemporaryUserStatus,
    createTemporaryUser,
    createTemporaryUserIsLoading,
    createTemporaryUserIsSuccess,
    createTemporaryUserIsError,

    revokeTemporaryAccess,
    revokeTemporaryAccessIsLoading: revokeTemporaryUserIsLoading || revokeTemporaryChannelAccessIsLoading,

    closeRevokeTemporaryUserConfirmationPopup,
    revokeTemporaryUserConfirmationPopupIsOpen,
    revokeTemporaryUserConfirmationAction,

    isTemporaryUserPeriodValid,

    readOrganisationUnitsForTemporaryAccessData,
    readOrganisationUnitsForTemporaryAccessIsSuccess,

    readRolesForTemporaryAccess,
    readRolesForTemporaryAccessData,
    readRolesForTemporaryAccessIsFetching,
    readRolesForTemporaryAccessIsSuccess,
    readRolesForTemporaryAccessError: readRolesForTemporaryAccessError ?? undefined,

    temporaryUserDetailsPopupIsOpen,
    closeTemporaryUserDetailsPopup,
    openTemporaryUserDetailsPopup,

    selectedTemporaryUserId,
    selectTemporaryUser,

    detailsViewingMode,

    refresh,
    temporaryAccessFilterHook,
  };
};

export default useTemporaryAccess;
